import { Link } from 'react-router-dom';
import { Phone, Users, BarChart3, Settings, Headphones, UserCircle } from 'lucide-react';
import { AdminPageLayout, StatsGrid } from '@/components/layout/AdminPageLayout';
import { StatCard } from '@/components/layout/AdminStatCard';
import { Card } from '@/components/ui/card';

const CallCenterOverview = () => {
  return (
    <AdminPageLayout
      title="Call Center AI System"
      subtitle="AI-powered call center with real-time agent assist, automatic QA, and DNI tracking"
    >

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Agent Desktop */}
        <Link
          to="/call-center/agent"
          className="block p-6 bg-white rounded-lg hover:shadow-lg transition-all border border-gray-200 hover:border-blue-300"
        >
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Headphones className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Agent Desktop</h3>
              <p className="text-sm text-gray-600">Handle calls with AI assist</p>
            </div>
          </div>
          <p className="text-sm text-gray-600">
            Real-time transcription, script adherence tracking, AI-powered suggestions, and knowledge base search during live calls.
          </p>
        </Link>

        {/* Manager Dashboard */}
        <Link
          to="/call-center/manager"
          className="block p-6 bg-white rounded-lg hover:shadow-lg transition-all border border-gray-200 hover:border-purple-300"
        >
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <UserCircle className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Manager Dashboard</h3>
              <p className="text-sm text-gray-600">Real-time supervision</p>
            </div>
          </div>
          <p className="text-sm text-gray-600">
            Monitor active calls, view risk alerts, track agent performance, whisper coaching, and barge-in capabilities.
          </p>
        </Link>

        {/* Call Analytics */}
        <Link
          to="/contact-center/analytics"
          className="block p-6 bg-white rounded-lg hover:shadow-lg transition-all border border-gray-200 hover:border-green-300"
        >
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Call Analytics</h3>
              <p className="text-sm text-gray-600">View metrics</p>
            </div>
          </div>
          <p className="text-sm text-gray-600">
            Call volume trends, conversion rates, script adherence scores, and performance leaderboards.
          </p>
        </Link>

        {/* DNI Attribution */}
        <Link
          to="/contact-center/dni-attribution"
          className="block p-6 bg-white rounded-lg hover:shadow-lg transition-all border border-gray-200 hover:border-orange-300"
        >
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <Phone className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">DNI Attribution</h3>
              <p className="text-sm text-gray-600">Track campaigns</p>
            </div>
          </div>
          <p className="text-sm text-gray-600">
            Dynamic number insertion, campaign tracking, multi-touch attribution, and ROI reporting.
          </p>
        </Link>

        {/* Queue Management */}
        <Link
          to="/contact-center/queue-management"
          className="block p-6 bg-white rounded-lg hover:shadow-lg transition-all border border-gray-200 hover:border-cyan-300"
        >
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-cyan-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Queue Management</h3>
              <p className="text-sm text-gray-600">Configure queues</p>
            </div>
          </div>
          <p className="text-sm text-gray-600">
            Call routing, queue statistics, hold time monitoring, and agent availability management.
          </p>
        </Link>

        {/* Settings */}
        <Link
          to="/contact-center/settings"
          className="block p-6 bg-white rounded-lg hover:shadow-lg transition-all border border-gray-200 hover:border-gray-400"
        >
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
              <Settings className="w-6 h-6 text-gray-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Call Center Settings</h3>
              <p className="text-sm text-gray-600">System config</p>
            </div>
          </div>
          <p className="text-sm text-gray-600">
            Script rules, objection library, knowledge base, Twilio integration, and system configuration.
          </p>
        </Link>
      </div>

      {/* System Status */}
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">System Status</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-gray-600">Edge Functions</p>
            <p className="text-2xl font-bold text-green-600">5 Active</p>
            <p className="text-xs text-gray-500 mt-1">AI Gateway, Call Grader, Twilio Handler, DNI Service, Health Check</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Database Tables</p>
            <p className="text-2xl font-bold text-blue-600">11 Tables</p>
            <p className="text-xs text-gray-500 mt-1">Agents, Calls, Transcripts, Grades, KB, DNI</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Knowledge Base</p>
            <p className="text-2xl font-bold text-purple-600">Ready</p>
            <p className="text-xs text-gray-500 mt-1">Vector embeddings, semantic search</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">AI Features</p>
            <p className="text-2xl font-bold text-orange-600">Enabled</p>
            <p className="text-xs text-gray-500 mt-1">Real-time STT, LLM, TTS, RAG</p>
          </div>
        </div>
      </Card>

      {/* Quick Links */}
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Quick Links</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <a href="https://console.twilio.com" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-400 hover:underline">
            Twilio Console →
          </a>
          <a href="https://platform.openai.com" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-400 hover:underline">
            OpenAI Platform →
          </a>
          <Link to="/monitoring" className="text-sm text-blue-400 hover:underline">
            System Monitoring →
          </Link>
          <Link to="/errors" className="text-sm text-blue-400 hover:underline">
            Error Tracking →
          </Link>
          <Link to="/analytics" className="text-sm text-blue-400 hover:underline">
            Usage Analytics →
          </Link>
          <Link to="/performance" className="text-sm text-blue-400 hover:underline">
            Performance Metrics →
          </Link>
        </div>
      </Card>
    </AdminPageLayout>
  );
};

export default CallCenterOverview;
